import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type QueryConstraint,
  getDocs,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { currentUser } from "./auth";
import type { BookingPage, CalendarEvent, FocusSession, Meeting, Note, NoteType, Preferences, ResearchItem, Source, Task, TranscriptSegment } from "./types";
import { defaultPreferences } from "./types";

const uid = () => {
  const u = currentUser();
  if (!u) throw new Error("Not signed in");
  return u.uid;
};
const userCol = (name: string) => collection(db, "users", uid(), name);
const asDate = (v: unknown): Date | null => (v instanceof Timestamp ? v.toDate() : v instanceof Date ? v : null);

/** Idempotency + provenance envelope on every write (W2-02). */
const envelope = (source: string) => ({ updatedAt: serverTimestamp(), actor: uid(), source, mutationId: crypto.randomUUID() });

// ---------- Tasks ----------
function toTask(id: string, d: DocumentData): Task {
  return {
    id,
    title: d.title ?? "",
    notes: d.notes ?? "",
    project: d.project ?? null,
    status: d.status ?? "open",
    source: d.source ?? "user",
    priority: d.priority ?? 1,
    durationMin: d.durationMin ?? 30,
    dueAt: asDate(d.dueAt),
    scheduledStart: asDate(d.scheduledStart),
    scheduledEnd: asDate(d.scheduledEnd),
    eventId: d.eventId ?? null,
    meetingId: d.meetingId ?? null,
    evidence: d.evidence ?? null,
    confidence: d.confidence ?? null,
    createdAt: asDate(d.createdAt) ?? new Date(),
    completedAt: asDate(d.completedAt),
  };
}

export function watchTasks(cb: (tasks: Task[]) => void) {
  return onSnapshot(query(userCol("tasks"), orderBy("createdAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => toTask(d.id, d.data()))),
  );
}

/** A pasted paragraph becomes a short title plus notes, so lists and the agenda stay readable. */
export function splitTitle(raw: string, notes = ""): { title: string; notes: string } {
  const text = raw.trim();
  const firstLine = text.split(/\r?\n/)[0]!.trim();
  if (text.length <= 120 && firstLine === text) return { title: text, notes };
  const sentence = /^(.{20,120}?[.!?])(\s|$)/.exec(firstLine)?.[1];
  let title = sentence ?? firstLine.slice(0, 120);
  if (!sentence && firstLine.length > 120) title = `${firstLine.slice(0, 117).replace(/\s+\S*$/, "")}…`;
  const rest = text.slice(0, 4000);
  return { title, notes: [notes.trim(), rest].filter(Boolean).join("\n\n") };
}

export async function createTask(input: Partial<Task> & { title: string }) {
  const { title, notes } = splitTitle(input.title, input.notes ?? "");
  const ref = await addDoc(userCol("tasks"), {
    title,
    notes,
    status: input.status ?? "open",
    priority: input.priority ?? 1,
    durationMin: input.durationMin ?? 30,
    dueAt: input.dueAt ?? null,
    scheduledStart: null,
    scheduledEnd: null,
    meetingId: input.meetingId ?? null,
    evidence: input.evidence ?? null,
    confidence: input.confidence ?? null,
    createdAt: serverTimestamp(),
    completedAt: null,
    ...envelope(input.source ?? "user"),
  });
  return ref.id;
}

export async function updateTask(id: string, patch: Partial<Task>, source = "user") {
  await updateDoc(doc(userCol("tasks"), id), { ...patch, ...envelope(source) });
}

export async function completeTask(id: string, done = true) {
  await updateTask(id, { status: done ? "done" : "open", completedAt: done ? new Date() : null });
}

/** Deleting a task also removes the block(s) Timo scheduled for it, so nothing stale stays on the calendar. */
export async function deleteTask(id: string) {
  const linked = await getDocs(query(userCol("calendarEvents"), where("taskId", "==", id)));
  await Promise.all(linked.docs.map((d) => deleteDoc(d.ref)));
  await deleteDoc(doc(userCol("tasks"), id));
}

// ---------- Calendar events ----------
function toEvent(id: string, d: DocumentData): CalendarEvent {
  return {
    id,
    title: d.title ?? "(untitled)",
    start: asDate(d.start) ?? new Date(),
    end: asDate(d.end) ?? new Date(),
    kind: d.kind ?? "event",
    flexible: !!d.flexible,
    location: d.location ?? "",
    meetLink: d.meetLink ?? "",
    attendees: d.attendees ?? 0,
    source: d.source ?? "user",
    googleId: d.googleId ?? null,
    taskId: d.taskId ?? null,
    notes: d.notes ?? "",
  };
}

export function watchEvents(from: Date, to: Date, cb: (events: CalendarEvent[]) => void) {
  const constraints: QueryConstraint[] = [where("start", ">=", from), where("start", "<", to), orderBy("start")];
  return onSnapshot(query(userCol("calendarEvents"), ...constraints), (snap) =>
    cb(snap.docs.map((d) => toEvent(d.id, d.data()))),
  );
}

export async function createEvent(input: Omit<CalendarEvent, "id">) {
  const ref = await addDoc(userCol("calendarEvents"), { ...input, ...envelope(input.source) });
  return ref.id;
}

export async function updateEvent(id: string, patch: Partial<CalendarEvent>) {
  await updateDoc(doc(userCol("calendarEvents"), id), { ...patch, ...envelope("user") });
}

export async function deleteEvent(id: string) {
  await deleteDoc(doc(userCol("calendarEvents"), id));
}

// ---------- Meetings ----------
function toMeeting(id: string, d: DocumentData): Meeting {
  return {
    id,
    title: d.title ?? "Meeting",
    status: d.status ?? "recording",
    captureSource: d.captureSource,
    startedAt: asDate(d.startedAt) ?? new Date(),
    endedAt: asDate(d.endedAt),
    durationSec: d.durationSec ?? 0,
    chunkCount: d.chunkCount ?? 0,
    transcribedCount: d.transcribedCount ?? 0,
    summarySoFar: d.summarySoFar ?? "",
    summary: d.summary ?? "",
    keyPoints: d.keyPoints ?? [],
    decisions: d.decisions ?? [],
    actionItems: d.actionItems ?? [],
    entities: d.entities ?? [],
    urls: d.urls ?? [],
    projects: d.projects ?? [],
    openQuestions: d.openQuestions ?? [],
    researchTopics: d.researchTopics ?? [],
    manualNotes: d.manualNotes ?? [],
    notesConfirmed: !!d.notesConfirmed,
    shareId: d.shareId ?? null,
    costUsd: d.costUsd ?? 0,
    silentChunks: d.silentChunks ?? 0,
    summaryMode: d.summaryMode ?? "",
    detailedNotes: d.detailedNotes ?? "",
    audioRetention: d.audioRetention ?? "delete-after-processing",
  };
}

export function watchMeetings(cb: (meetings: Meeting[]) => void) {
  return onSnapshot(query(userCol("meetings"), orderBy("startedAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => toMeeting(d.id, d.data()))),
  );
}

export function watchMeeting(id: string, cb: (m: Meeting | null) => void) {
  return onSnapshot(doc(userCol("meetings"), id), (snap) => cb(snap.exists() ? toMeeting(snap.id, snap.data()) : null));
}

export async function createMeeting(title: string) {
  const ref = await addDoc(userCol("meetings"), {
    title,
    status: "recording",
    startedAt: serverTimestamp(),
    endedAt: null,
    durationSec: 0,
    chunkCount: 0,
    transcribedCount: 0,
    audioRetention: "delete-after-processing",
    ...envelope("user"),
  });
  return ref.id;
}

export async function updateMeeting(id: string, patch: Record<string, unknown>) {
  await updateDoc(doc(userCol("meetings"), id), { ...patch, ...envelope("user") });
}

export async function writeChunkMeta(meetingId: string, seq: number, meta: Record<string, unknown>) {
  await setDoc(doc(userCol("meetings"), meetingId, "chunks", String(seq).padStart(5, "0")), { seq, ...meta, updatedAt: serverTimestamp() }, { merge: true });
}

export function watchTranscript(meetingId: string, cb: (segments: TranscriptSegment[]) => void) {
  return onSnapshot(query(collection(userCol("meetings"), meetingId, "transcriptSegments"), orderBy("seq")), (snap) =>
    cb(snap.docs.map((d) => ({ seq: d.data().seq, text: d.data().text ?? "", offsetSec: d.data().offsetSec ?? 0, durationSec: d.data().durationSec ?? 0 }))),
  );
}

// ---------- Preferences ----------
export function watchPreferences(cb: (p: Preferences) => void) {
  return onSnapshot(doc(db, "users", uid()), (snap) => cb({ ...defaultPreferences(), ...(snap.data()?.preferences ?? {}) }));
}

export async function savePreferences(patch: Partial<Preferences>) {
  const flat: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) flat[`preferences.${k}`] = v;
  await updateDoc(doc(db, "users", uid()), { ...flat, updatedAt: serverTimestamp() });
}

// ---------- Notes ----------
export function watchNotes(cb: (notes: Note[]) => void) {
  return onSnapshot(query(userCol("notes"), orderBy("updatedAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => ({
      id: d.id,
      title: d.data().title ?? "Untitled",
      body: d.data().body ?? "",
      type: d.data().type ?? "personal",
      meetingId: d.data().meetingId ?? null,
      linkedTasks: d.data().linkedTasks ?? 0,
      updatedAt: asDate(d.data().updatedAt) ?? new Date(),
      createdAt: asDate(d.data().createdAt) ?? new Date(),
    }))),
  );
}
export interface SetupFlags { calendarConfigured: boolean; emailConfigured: boolean }
/** Setup flags an admin controls. Readable by any signed-in user; contains no keys. */
export function watchSetupFlags(cb: (f: SetupFlags) => void) {
  return onSnapshot(doc(db, "config/public"), (snap) => cb({ calendarConfigured: !!snap.data()?.calendarConfigured, emailConfigured: !!snap.data()?.emailConfigured }), () => cb({ calendarConfigured: false, emailConfigured: false }));
}

export async function createNote(input: { title: string; body?: string; type?: NoteType }) {
  const ref = await addDoc(userCol("notes"), { title: input.title, body: input.body ?? "", type: input.type ?? "personal", meetingId: null, createdAt: serverTimestamp(), ...envelope("user") });
  return ref.id;
}
export async function updateNote(id: string, patch: Partial<Note>) {
  await updateDoc(doc(userCol("notes"), id), { ...patch, ...envelope("user") });
}
export async function deleteNote(id: string) {
  await deleteDoc(doc(userCol("notes"), id));
}

// ---------- Research ----------
export function watchResearch(cb: (items: ResearchItem[]) => void) {
  return onSnapshot(query(userCol("research"), orderBy("createdAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => ({
      id: d.id,
      topic: d.data().topic ?? "",
      detail: d.data().detail ?? "",
      status: d.data().status ?? "open",
      source: d.data().source ?? "user",
      meetingId: d.data().meetingId ?? null,
      url: d.data().url ?? null,
      createdAt: asDate(d.data().createdAt) ?? new Date(),
    }))),
  );
}
export async function createResearch(input: { topic: string; detail?: string; url?: string | null; source?: Source }) {
  const ref = await addDoc(userCol("research"), { topic: input.topic, detail: input.detail ?? "", url: input.url ?? null, status: "open", meetingId: null, createdAt: serverTimestamp(), ...envelope(input.source ?? "user") });
  return ref.id;
}
export async function updateResearch(id: string, patch: Partial<ResearchItem>) {
  await updateDoc(doc(userCol("research"), id), { ...patch, ...envelope("user") });
}
export async function deleteResearch(id: string) {
  await deleteDoc(doc(userCol("research"), id));
}

// ---------- Focus sessions ----------
export function watchFocusSessions(cb: (s: FocusSession[]) => void) {
  return onSnapshot(query(userCol("focusSessions"), orderBy("startedAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, taskId: d.data().taskId ?? null, title: d.data().title ?? "", seconds: d.data().seconds ?? 0, startedAt: asDate(d.data().startedAt) ?? new Date(), endedAt: asDate(d.data().endedAt) ?? new Date() }))),
  );
}
export async function saveFocusSession(input: { taskId: string | null; title: string; seconds: number; startedAt: Date }) {
  await addDoc(userCol("focusSessions"), { ...input, endedAt: new Date(), ...envelope("user") });
}

// ---------- Booking page (owner side) ----------
export function watchBookingPage(cb: (p: BookingPage | null) => void) {
  return onSnapshot(doc(userCol("bookingPages"), "default"), (snap) => cb(snap.exists() ? (snap.data() as BookingPage) : null));
}


export async function addManualNote(meetingId: string, note: { at: string; text: string }) {
  await updateDoc(doc(userCol("meetings"), meetingId), { manualNotes: arrayUnion({ ...note, createdAt: new Date() }), ...envelope("user") });
}
